<script setup lang="ts">
import type { ClassValue } from 'tailwind-variants';
import { computed, ref, useSlots } from 'vue';
import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
    VisuallyHidden,
} from 'reka-ui';
import { IconX } from '@tabler/icons-vue';
import { dialogVariants } from './index.ts';

type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const props = withDefaults(defineProps<{
    /**
     * Heading text shown inside the auto-rendered header.
     * If omitted (and no `#header` slot is provided) the header is not rendered.
     */
    title?: string;
    /**
     * Subtitle text rendered below the title. Wired to `aria-describedby` for free.
     */
    description?: string;
    /**
     * Renders the built-in X close button in the top-right corner.
     * @default true
     */
    closable?: boolean;
    /**
     * When `false`, the dialog cannot be dismissed via ESC or click/focus outside.
     * Explicit close controls can still close the dialog.
     * @default true
     */
    dismissible?: boolean;
    /**
     * Width preset for the panel.
     * @values 'sm', 'md', 'lg', 'xl'
     * @default 'md'
     */
    size?: DialogSize;
    /**
     * Forwarded to reka-ui's `DialogRoot`. When `false`, the dialog renders without
     * a focus trap or scroll lock. Rarely needed.
     * @default true
     */
    modal?: boolean;
    /**
     * Whether to render the backdrop overlay behind the panel.
     * @default true
     */
    overlay?: boolean;
    /**
     * Extra classes merged onto the panel. Use to override width / padding at call sites.
     */
    contentClass?: ClassValue;
}>(), {
    title: undefined,
    description: undefined,
    closable: true,
    dismissible: true,
    size: 'md',
    modal: true,
    overlay: true,
    contentClass: undefined,
});

const emit = defineEmits<{
    'after:enter': [];
    'after:leave': [];
}>();

const isOpen = defineModel<boolean | undefined>('open', { default: undefined });

const slots = useSlots();

const ui = computed(() => dialogVariants({ size: props.size }));

const isExplicitClose = ref(false);

const onUpdateOpen = (value: boolean) => {
    if (value || props.dismissible || isExplicitClose.value) {
        isOpen.value = value;
    }

    isExplicitClose.value = false;
};
</script>

<template>
    <DialogRoot
        :open="isOpen"
        :modal="modal"
        @update:open="onUpdateOpen"
    >
        <DialogTrigger
            v-if="slots.trigger"
            as-child
        >
            <slot name="trigger" />
        </DialogTrigger>

        <DialogPortal>
            <DialogOverlay
                v-if="overlay"
                :class="ui.overlay()"
            />
            <DialogContent
                :class="ui.content({ class: contentClass })"
                @after-enter="emit('after:enter')"
                @after-leave="emit('after:leave')"
            >
                <VisuallyHidden v-if="(!title && !slots.header) || !description">
                    <DialogTitle v-if="!title && !slots.header" />
                    <DialogDescription v-if="!description" />
                </VisuallyHidden>

                <header
                    v-if="title || description || slots.header"
                    :class="ui.header()"
                >
                    <DialogTitle
                        v-if="title"
                        :class="ui.title()"
                    >
                        {{ title }}
                    </DialogTitle>
                    <DialogTitle
                        v-else-if="slots.header"
                        :class="ui.title()"
                    >
                        <slot name="header" />
                    </DialogTitle>
                    <DialogDescription
                        v-if="description"
                        :class="ui.description()"
                    >
                        {{ description }}
                    </DialogDescription>
                    <slot
                        v-if="title"
                        name="header"
                    />
                </header>

                <div :class="ui.body()">
                    <slot />
                </div>

                <footer
                    v-if="slots.footer"
                    :class="ui.footer()"
                >
                    <slot name="footer" />
                </footer>

                <DialogClose
                    v-if="closable"
                    :class="ui.close()"
                    aria-label="Sluiten"
                    @click="isExplicitClose = true"
                >
                    <IconX
                        class="size-6"
                        aria-hidden="true"
                    />
                </DialogClose>
            </DialogContent>
        </DialogPortal>
    </DialogRoot>
</template>
